import {
  CPR_PLACEHOLDER_TOKENS,
  buildPlaceholderDictionary,
  fillPlaceholderText,
  reportPlaceholderContext,
} from '@/lib/patientReports/placeholders';
import {
  resolveReportPreview,
  type ReportPreviewModel,
  type ReportSection,
} from '@/lib/patientReports/template';

export type PersonalTextFill = {
  /** type id -> this user's authored template. Not a patient value. */
  bodies: ReadonlyMap<string, string>;
  authorName: string;
  clinicName: string;
  generatedAt: Date;
  missingText: string;
  chooseText: string;
};

type PreviewInput = Parameters<typeof resolveReportPreview>[0];

/**
 * Resolve a report, then fill personal texts with the same placeholder dictionary
 * the Word template uses. Personal texts are omitted from that dictionary's
 * [Indhold], so a text cannot include itself.
 */
export function resolvePreviewWithPersonalTexts(
  input: PreviewInput,
  fill: PersonalTextFill,
): ReportPreviewModel {
  const shell = resolveReportPreview({
    ...input,
    blankUserText: true,
  });
  const dictionary = buildPlaceholderDictionary(
    reportPlaceholderContext({
      reportTitle: input.title,
      authorName: fill.authorName,
      clinicName: fill.clinicName,
      generatedAt: fill.generatedAt,
      patient: input.catalog.patient,
      cprAccess: input.cprAccess,
      cpr: input.cpr,
      cprHidden: input.labels.cprHidden,
      cprLoading: input.labels.cprLoading,
      coverTitle: input.coverTitle,
      coverBody: input.coverBody,
      sections: input.sections,
      model: shell,
    }),
  );
  return resolveReportPreview({
    ...input,
    userTexts: fill.bodies,
    fillText: (body) => fillPlaceholderText(body, dictionary),
    missingUserText: input.showEmpty ? fill.missingText : '',
    chooseUserText: input.showEmpty ? fill.chooseText : '',
  });
}

export function personalTextBodies(
  texts: readonly { typeId: string; body: string }[],
): Map<string, string> {
  const bodies = new Map<string, string>();
  for (const text of texts) bodies.set(text.typeId, text.body);
  return bodies;
}

/** CPR is fetched only when a personal text on this report actually contains the token. */
export function personalTextNeedsCpr(
  sections: readonly ReportSection[],
  bodies: ReadonlyMap<string, string>,
): boolean {
  return sections.some((section) => {
    if (section.kind !== 'user_text' || !section.typeId) return false;
    const body = bodies.get(section.typeId) ?? '';
    return CPR_PLACEHOLDER_TOKENS.some((token) => body.includes(token));
  });
}
